import { Form } from "antd";
import { Alert } from "antd-v5";
import { BaseModalRef } from "components/BaseModal";
import message from "components/CustomMessage";
import Vocabulary, { getVocabulary } from "components/Vocabulary";
import { PermissionSetMap } from "constants/RBACConstants";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import { FindingBucketContextType } from "contexts/FindingBucketContext";
import useAPIResponse from "hooks/useAPIResponse";
import _ from "lodash";
import moment from "moment";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import ExceptionAPI from "restAPI/ExceptionAPI";
import FindingAPI from "restAPI/FindingAPI";
import GlobalSettingAPI from "restAPI/GlobalSettingAPI";
import { ProjectResponseDto } from "types/api/projectResponseDto";
import { hasPermission } from "utils/authService";
import { FieldTypes } from "utils/formUtils";
import { formatNumber } from "utils/numberUtil";

import ACSegmented from "ac-components/ac-segmented/ACSegmented";

import BulkUpdatePopup from "./BulkUpdatePopup";

type Props = {
  afterClose: () => void;
  bucket: FindingBucketContextType;
  history: any;
};

const AddToExceptionPopup = forwardRef<BaseModalRef, Props>(
  ({ afterClose, bucket, history }, ref) => {
    const [form] = Form.useForm();
    const [segmentValue, setSegmentValue] = useState("existing");
    const [exceptionConfig, exceptionLoading] = useAPIResponse(
      () => GlobalSettingAPI.getExceptionConfig(),
      [],
    );
    const featureFlags = useFeatureFlag();
    const totalFindingsCount = bucket.totalCount;
    const groupFilters = bucket.findingFilterGroups();

    useEffect(() => {
      if (form && exceptionConfig) {
        form.setFieldsValue({
          startDate: moment(),
          endDate: moment().add(
            exceptionConfig?.maxExceptionDurationInDays,
            "days",
          ),
        });
      }
    }, [exceptionConfig, form]);

    const handleOnOk = useCallback(
      async (data: any) => {
        if (data?.exception === "new") {
          data.findingFilterGroups = groupFilters;
          await ExceptionAPI.requestNewException(data).promise;
        } else {
          data.riskRegister = _.isArray(data.riskRegister)
            ? data.riskRegister
            : [data.riskRegister];
          await bucket.submit(data, FindingAPI.bulk.addToException);
        }

        message.success(
          <>
            {totalFindingsCount} finding(s) added to{" "}
            <Vocabulary name="exception" />. Please check after some time.
          </>,
        );
      },
      [bucket, totalFindingsCount, groupFilters],
    );

    const onExceptionCreation = useCallback(
      (createdExceptionData) => {
        const currentValues = form.getFieldsValue(["riskRegister"]);
        form.setFieldsValue({
          riskRegister: [
            createdExceptionData.id,
            ...(currentValues?.riskRegister || []),
          ],
        });
      },
      [form],
    );

    const fields = useMemo(
      () => [
        {
          name: "",
          render: () => (
            <div>
              <Alert
                message={
                  <>
                    Findings that are already associated with an{" "}
                    <Vocabulary name="exception" /> will be removed from old{" "}
                    <Vocabulary name="exception" /> and added to new{" "}
                    <Vocabulary name="exception" />.
                  </>
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              {exceptionConfig?.multipleExceptionSupport === false && (
                <Alert
                  type="warning"
                  className="m-b-sm"
                  showIcon
                  message={
                    <>
                      Multiple <Vocabulary name="exception" /> Support is
                      disabled from global config. To select multiple{" "}
                      <Vocabulary name="exception" isPlural /> enable it from
                      global config
                    </>
                  }
                  style={{ marginBottom: 16 }}
                />
              )}
            </div>
          ),
        },
        ...(featureFlags.EXCEPTION_MODULE &&
        hasPermission(PermissionSetMap.CREATE_EXCEPTION)
          ? [
              {
                name: "exception",
                render: () => (
                  <ACSegmented
                    className="m-b-sm"
                    items={[
                      {
                        label: `Existing ${getVocabulary("exception")}`,
                        value: "existing",
                      },
                      {
                        label: `Request New ${getVocabulary("exception")}`,
                        value: "new",
                      },
                    ]}
                    onChange={(value) => setSegmentValue(value as string)}
                    value={segmentValue}
                  />
                ),
              },
            ]
          : []),
        ...(segmentValue === "existing"
          ? [
              {
                name: "riskRegister",
                label: (
                  <>
                    Select <Vocabulary name="exception" />
                  </>
                ),
                type: FieldTypes.EXCEPTION_SELECT_ADD,
                props: {
                  mode:
                    exceptionConfig?.multipleExceptionSupport === true
                      ? "multiple"
                      : undefined,
                  onCreate: (createdProject: ProjectResponseDto) => {
                    onExceptionCreation(createdProject);
                  },
                  loading: exceptionLoading,
                  disabled: exceptionLoading,
                  history,
                  bucket,
                  showAddButton: !featureFlags.EXCEPTION_MODULE,
                },
              },
            ]
          : [
              {
                name: "startDate",
                label: "Start date",
                type: FieldTypes.DATE,
                colSpan: 12,
                required: true,
                props: {
                  style: { width: "100%" },
                  disabledDate: (current: {
                    isAfter: (arg0: moment.Moment, arg1: string) => any;
                  }) => {
                    return (
                      current &&
                      (current.isAfter(moment().add(2, "years"), "days") ||
                        (form?.getFieldValue("endDate") &&
                          current.isAfter(
                            form.getFieldValue("endDate"),
                            "day",
                          )))
                    );
                  },
                  onChange: (value: moment.MomentInput) => {
                    if (value) {
                      form.setFields([
                        {
                          name: "endDate",
                          value: moment(value).add(
                            exceptionConfig?.maxExceptionDurationInDays,
                            "days",
                          ),
                        },
                      ]);
                    }
                  },
                },
              },
              {
                name: "endDate",
                label: "End date",
                type: FieldTypes.DATE,
                required: true,
                props: {
                  style: { width: "100%" },
                  disabledDate: (current: {
                    isBefore: (arg0: any, arg1: string) => any;
                    isAfter?: (arg0: any, arg1: string) => any;
                  }) => {
                    const startDate = form?.getFieldValue("startDate");
                    if (
                      !current ||
                      !startDate ||
                      !exceptionConfig?.maxExceptionDurationInDays
                    ) {
                      return false;
                    }

                    const maxEndDate = moment(startDate).add(
                      exceptionConfig?.maxExceptionDurationInDays,
                      "days",
                    );

                    return (
                      current.isBefore(moment(startDate), "day") ||
                      (current.isAfter && current.isAfter(maxEndDate, "day"))
                    );
                  },
                },
                colSpan: 12,
                rules: [
                  {
                    validator: (_: any, value: moment.MomentInput) => {
                      const startDate = form.getFieldValue("startDate");
                      if (value && moment(value).isSame(startDate, "day")) {
                        return Promise.reject(
                          "The end date cannot be same as start date. Please choose a valid end date.",
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ],
              },
              {
                name: "reasons",
                label: "Reasons",
                type: FieldTypes.MULTI_SELECT,
                props: {
                  api: ExceptionAPI.getReasons,
                },
                colSpan: 12,
                help: <>Reasons can be configured from global settings.</>,
              },
            ]),
      ],
      [
        featureFlags.EXCEPTION_MODULE,
        segmentValue,
        exceptionConfig?.multipleExceptionSupport,
        exceptionConfig?.maxExceptionDurationInDays,
        exceptionLoading,
        history,
        bucket,
        onExceptionCreation,
        form,
      ],
    );

    const modalProps = useMemo(
      () => ({
        okText: (
          <>
            {segmentValue === "existing" ? "Add to" : "Request"}{" "}
            <Vocabulary name="exception" />
          </>
        ),
      }),
      [segmentValue],
    );

    return (
      <BulkUpdatePopup
        ref={ref}
        title={
          <>
            Add to <Vocabulary name="exception" />
            <div className="pull-right text-bold info">
              {formatNumber(totalFindingsCount, "long")} findings
            </div>
          </>
        }
        form={form}
        onOk={handleOnOk}
        bucket={bucket}
        fields={fields}
        modalProps={modalProps}
        afterClose={afterClose}
        initialValues={{ exception: segmentValue }}
      />
    );
  },
);

export default AddToExceptionPopup;
